<template>
<div class="erm-change-list">
  <v-card>
    <!-- 頁面標題列（共用元件，融入卡片頂部） -->
    <ListPageHeader
      embedded
      title="經費計畫變更清單"
      subtitle="管理與追蹤各項經費計畫變更申請進度"
      icon="tabler-file-diff"
      :count="pageCount"
    />

    <v-card-item>
      <div class="d-flex align-center mb-1">
        <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
        <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
      </div>
      <v-row class="pt-2 pb-5">
        <v-col cols="12" md="2">
          <v-text-field
            v-model="filters.changeNo"
            label="申請單號"
            clearable
            prepend-inner-icon="tabler-search"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            :items="years"
            label="所屬年度"
            v-model="filters.year"
            clearable
            item-title="rocYear"
            item-value="adYear"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            item-value="projectId"
            item-title="projectTitle"
            :items="projectNames"
            label="項目名稱"
            v-model="filters.projectName"
            clearable
            no-data-text="無項目名稱"
            prepend-inner-icon="tabler-search"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <AppInput
            v-model="filters.status"
            type="select"
            :remoteItem="deSelectSelect"
            :remoteItemParams="{ tabId}"
            item-title="label"
            item-value="value"
            placeholder="請輸入狀態"
            :disabled="false"
            :loading="false"
            clearable
          />
        </v-col>
      </v-row>
    </v-card-item>
    <InteractiveDataTable
    v-model:items-per-page="filters.size"
    v-model:page="filters.page"
    :items-length="pageCount"
    :headers="headers" 
    :items="items" 
    :loading="loading"
    empty-text="尚無經費計畫變更申請"
    empty-subtitle="可調整上方篩選條件後再查詢"
    empty-icon="tabler-file-diff"
    :items-per-page-options="[
      {value: 10, title: '10'},
      {value: 25, title: '25'},
      {value: 50, title: '50'},
      {value: 100, title: '100'},
    ]"
    :row-props="rowProps"
    :row-clickable="false"
    @row-click="markLastViewed"
    >
      <template #item.changeNo="{ item }">
        <div class="d-inline-flex align-center flex-nowrap">
          <v-btn 
           @click.stop="edit(item)"
           color="primary" 
           variant="text" 
           class="pa-0 idt-link font-weight-bold"
           >
             {{ toText(item.changeNo) }}
           </v-btn>
          <v-tooltip location="top" text="複製單號">
            <template #activator="{ props: tipProps }">
              <v-btn
                v-bind="tipProps"
                variant="text"
                icon
                size="x-small"
                density="compact"
                class="ms-1"
                @click.stop="notifyCopyDocNo(String(toText(item.changeNo) || item.changeNo || ''))"
              >
                <VIcon icon="tabler-copy" size="18" />
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <Status 
            :status="item.status"
            :is-red="['Rejected', 'Revoke', 'Revoked']"
            :is-green="['MApproved','FApproved','FPassed','SendAfterCorrect']"
            :is-warning="['Draft']"
            :is-info="[]"
            :is-purple="['ReturnedForRevision', 'MReturnedForRevision', 'FReturnedForRevision','CorrectAfterApprove']"
            :is-teal="['Reviewing']"
            :is-indigo="[]"
            :is-primary="['Submitted', 'MPassed', 'Resubmitted']"
            :shared-table-id="tabId"
            btn-class="x-large"
            font-class="18px"
            :status-config="getStatusData"
          />
      </template>
      <template v-slot:item.projectTitle="{ item }">
        <v-tooltip location="top" :disabled="!item.projectTitle || String(item.projectTitle).length < 18">
          <template #activator="{ props: tipProps }">
            <span v-bind="tipProps" class="idt-truncate text-truncate">
              {{ item.projectTitle || '-' }}
            </span>
          </template>
          <span>{{ item.projectTitle }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.applyDate="{ item }">
        <div class="d-inline-flex flex-column">
          <span class="d-inline-flex align-center text-medium-emphasis">
            <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
            {{ taiwanDateParts(item.applyDate).date || '-' }}
          </span>
          <span v-if="taiwanDateParts(item.applyDate).time" class="text-caption text-disabled ms-5">
            {{ taiwanDateParts(item.applyDate).time }}
          </span>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex">
          <v-btn 
          v-if="item.status=='Closed'"
          @click.stop="goToReimbuirsement(item)"
          size="default" color="primary" 
          prepend-icon="tabler-receipt"
          variant="tonal" 
          class="mr-2 idt-action-btn">
            核銷申請
          </v-btn>
        </div>
      </template>
    </InteractiveDataTable>
  </v-card>
</div>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, reactive } from "vue";
import { useLastViewedRow } from '@/composables/useLastViewedRow'
import { copyPlainText } from '@/utils/deUtil'
import Status from '../tnm/StatusComponenet.vue'

definePage({
  meta: {
    action: 'manage', //固定為 manage
    subject: 'EXPENSECHANGELIST', //系統代碼(系統授權設定中的系統代碼)
  },
})

const pageCount = ref(0)
const loading = ref(false)
const router = useRouter()
const filters =reactive({
  name: "",
  changeNo: "",
  year: null,
  projectName: null,
  status: null,
  page: 1,
  size: 100,
})

const FILTER_STATE_KEY = 'erm_expense_change_list_filter_state'
const restoreListFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTER_STATE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw) as Record<string, unknown>
    for (const k of Object.keys(filters) as (keyof typeof filters)[]) {
      if (k in saved) (filters as Record<string, unknown>)[k] = saved[k] as never
    }
  } catch { /* ignore */ }
}
const saveListFilters = () => {
  try {
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify({ ...filters }))
  } catch { /* ignore */ }
}
restoreListFilters()

const projectNames=reactive([])
const statuses=reactive(["狀態1", "狀態2"])
const headers=reactive([
  { title: "申請單號", key: "changeNo", icon: "tabler-file-text" },
  { title: "經費需求項目名稱", key: "projectTitle", icon: "tabler-clipboard-text" },
  { title: "變更原因", key: "changeReason", icon: "tabler-edit-circle" },
  { title: "申請日期", key: "applyDate", icon: "tabler-calendar-event" },
  { title: "狀態", key: "status", icon: "tabler-flag" },
  // { title: "操作", key: "actions", icon: "tabler-settings" },
])
const items=reactive([])
const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow('erm_expense_change_list_last_clicked', 'changeNo')

const markLastViewed = (item: any) => {
  setLastClickedId(item?.changeNo)
}

// 拆解民國年日期字串為「日期」與「時間」兩部分
const taiwanDateParts = (raw: string) => {
  const [date = '', time = ''] = convertToTaiwanYear(raw || '', '-').split(' ')

  return { date, time }
}

// 操作跳轉
const edit = async (item) =>{
  setLastClickedId(item.changeNo)
  await nextTick()
  if(item.fieldB=='S'){
    router.push({ path: '/tca/erm/ExpenseChangeSchedule', query: { changeNo: item.changeNo  } })
  }else{
    router.push({ path: '/tca/erm/ExpenseChange', query: { changeNo: item.changeNo } })
  }
}
// 頁面跳轉 - 核銷
const goToReimbuirsement = async(item) => {
  router.push({ path: '/tca/erm/ExpenseReimbursement', query: { applyNo: item.applyNo } })
}
// 操作跳轉
const goToChange = async(item) =>{
  const isExist = await isExistPreviosProject(item.applyNo)
  // true 是已有資料 false才可跳轉
  if(!isExist){
    router.push({ path: '/tca/erm/ExpenseChange', query: { changeNo: item.changeNo, isNew:"Y" } })
  }else{
    $swal({ title:`${item.applyNo}存在未結案的變更申請`, icon: "error" });
  }
}
// 檢查經費需求是否有其他會結案 API  applyNo為經費需求的applyNo 
const isExistPreviosProject = async (applyNo) => {
  try {
    const resp = await $api('/tca/expensechange/isExist', {
      method: 'POST', //依據後端 restful api 的 method 設定
      body: { applyNo } // request body
    });
    // true為已經存在其他changeNo
    // false為不存在任何changeNo
    return resp.data; // 正確返回值
  } catch (error) {
    console.error("Error fetching previous project existence:", error);
    return true; // 錯誤處理，返回 false 或其他合適的值
  }
};

// 所屬年度API
const { data: yearList, execute: fetchFilterYearList } = await useApi<any>(createUrl('/tca/expenserequestprojectlist/getYear', {
}))

const years = computed(() => yearList ? yearList.value.data : []);

// select 項目名稱
const getProjectList = async () => {
  let annual = filters.year?filters.year:null
  await $api('/tca/expenserequestprojectlist/getAllProjectList', {
    method: 'POST',
    body: {
      deptNo: null,
      isGold: null,
      annual,
    }
  }).then((response) => {
    projectNames.splice(0, projectNames.length, ...response.data)
  }).catch((error) => {
    console.error('取得項目下拉選單失敗:', error);
  });
};


// 
const fetchData =async() =>{
  let applyTitle = filters.name?filters.name:null
  let projectId = filters.projectName?filters.projectName:null
  let status = filters.status?filters.status:null
  let changeNo = filters.changeNo?filters.changeNo:null
  let page = filters.page-1
  let size = filters.size
  loading.value = true
  try {
    const response = await $api('/tca/expensechangelist/getList', {
      method: 'POST',
      body: {
        applyTitle,
        deptNo: null,
        projectId,
        changeNo,
        status,
        page,
        size,
      },
    });

    // 根据响应做一些处理
   if (response.data) {
      items.splice(0, items.length, ...response.data.requestList)
      pageCount.value = response.data.totalElements
    } else {
      // 处理错误情况
      items.splice(0, items.length)
      console.error('Fetch error:', response.statusText);
      pageCount.value = 0
    }
  } catch (error) {
    console.error('Error during fetch:', error);
  } finally {
    loading.value = false
  }
}

const tabId = ref('ExpenseChange')
const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))

const notifyCopyDocNo = async (text: string | null | undefined) => {
  const ok = await copyPlainText(text)
  if (ok) {
    $swal({
      icon: 'success',
      title: '已複製到剪貼簿',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
    })
  } else {
    $swal({
      icon: 'error',
      title: '複製失敗',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
    })
  }
}

watch(()=>filters.year,()=>{
  getProjectList()
})
watch(filters,()=>{
  fetchData()
})
onMounted(()=>{
  getProjectList()
  fetchData()
})

onUnmounted(() => saveListFilters())
</script>
